import { Search, ShoppingCart, User, Menu, Heart, LogOut, Package, Settings, X, Home, Phone, HelpCircle, MapPin, Sparkles } from 'lucide-react';
import { useState, useEffect, useRef } from 'react';
import { useCart } from '../CartContext';
import headerBg from '../../assets/15496945350b67a7b248006150bb217ebb850302.png';
import { toast } from 'sonner';

const API_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:5000'
  : 'https://kiyafet-magazasi-backend.vercel.app';

interface HeaderProps {
  onCategoryChange?: (category: string) => void;
  initialCategory?: string;
}

export function Header({ onCategoryChange, initialCategory }: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(() => {
    return initialCategory || localStorage.getItem('selectedCategory') || 'all';
  });
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const { cartCount } = useCart();
  const [kullanici, setKullanici] = useState<any>(null);
  const [categories, setCategories] = useState([{ id: 'all', name: 'Tümü' }]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const saved = localStorage.getItem('kullanici');
    if (saved) setKullanici(JSON.parse(saved));
    const handleStorage = () => {
      const updated = localStorage.getItem('kullanici');
      setKullanici(updated ? JSON.parse(updated) : null);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    fetch(`${API_URL}/api/kategoriler`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const apiCats = data
            .filter((c: any) => c.aktif !== false && c.ad !== 'Tümü')
            .map((c: any) => ({ id: c.ad, name: c.ad }));
          setCategories([...apiCats, { id: 'kesfet', name: 'Keşfet' }]);
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) { setSearchResults([]); return; }
    const timer = setTimeout(() => {
      fetch(`${API_URL}/api/urunler`)
        .then(r => r.json())
        .then(data => {
          if (Array.isArray(data)) {
            const q = searchQuery.toLowerCase();
            const results = data.filter((u: any) =>
              u.ad?.toLowerCase().includes(q) || u.kategori?.toLowerCase().includes(q) || u.marka?.toLowerCase().includes(q)
            ).slice(0, 6);
            setSearchResults(results);
            setShowSearch(results.length > 0);
          }
        }).catch(() => {});
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    function handleSearchClickOutside(e: MouseEvent) {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setShowSearch(false);
    }
    document.addEventListener('mousedown', handleSearchClickOutside);
    return () => document.removeEventListener('mousedown', handleSearchClickOutside);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setUserDropdownOpen(false);
      }
    }
    if (userDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userDropdownOpen]);

  const handleLogout = () => {
    localStorage.removeItem('kullanici');
    setKullanici(null);
    setUserDropdownOpen(false);
    toast.success('Çıkış yapıldı. Güle güle! 👋');
    window.location.href = '/';
  };

  const handleCategoryClick = (categoryId: string) => {
    if (categoryId === 'kesfet') {
      window.location.href = '/kesfet';
      return;
    }
    setActiveCategory(categoryId);
    if (onCategoryChange) onCategoryChange(categoryId);
    if (window.location.pathname !== '/') {
      localStorage.setItem('selectedCategory', categoryId);
      window.location.href = '/';
      return;
    }
    const element = document.getElementById('products-section');
    if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const isAuthenticated = !!kullanici;

  return (
    <header className="w-full sticky top-0 z-50">
      <div className="relative bg-cover bg-center" style={{ backgroundImage: `url(${headerBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="bg-red-900/30 backdrop-blur-sm">
          <div className="max-w-7xl mx-auto px-4 py-6">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <button className="md:hidden text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}><Menu className="size-6" /></button>
                <a href="/" className="text-white">
                  <div className="text-3xl md:text-4xl font-bold tracking-wider">LADIORA</div>
                  <div className="text-sm md:text-base tracking-widest">BOUTIQUE</div>
                </a>
              </div>

              <div className="hidden md:flex items-center bg-white/95 rounded-full px-4 py-2 flex-1 max-w-md mx-8 relative" ref={searchRef}>
                <input 
                  type="text" 
                  placeholder="Ladiora'da Ara" 
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setShowSearch(true); }}
                  onFocus={() => { if (searchResults.length > 0) setShowSearch(true); }}
                  className="bg-transparent outline-none text-sm flex-1 text-gray-700 placeholder-gray-500" 
                />
                <Search className="size-5 text-gray-500" />
                {showSearch && searchResults.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-50">
                    {searchResults.map((u: any) => (
                      <a key={u.id} href={`/urun/${u.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        onClick={() => { setShowSearch(false); setSearchQuery(''); }}>
                        <img src={u.resimler?.[0] || ''} alt={u.ad} className="size-12 rounded-lg object-cover bg-gray-100" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">{u.ad}</p>
                          <p className="text-xs text-gray-500">{u.kategori}</p>
                        </div>
                        <span className="text-sm font-bold text-pink-600">{Number(u.fiyat).toLocaleString('tr-TR')} TL</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex items-center gap-3">
                <button className="text-white hover:text-gray-200 transition-colors"><Search className="size-6 md:hidden" /></button>

                {isAuthenticated ? (
                  <div className="relative hidden md:block" ref={dropdownRef}>
                    <button onClick={() => setUserDropdownOpen(!userDropdownOpen)} className="flex items-center gap-2 text-white hover:text-gray-200 transition-colors bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                      <User className="size-5" />
                      <span className="text-sm font-medium">Hoşgeldin, {kullanici.ad} {kullanici.soyad}</span>
                    </button>
                    {userDropdownOpen && (
                      <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border-2 border-gray-100 overflow-hidden">
                        <div className="bg-gradient-to-r from-pink-500 to-purple-500 text-white p-4">
                          <p className="font-bold text-lg">{kullanici.ad} {kullanici.soyad}</p>
                          <p className="text-sm opacity-90">{kullanici.email}</p>
                        </div>
                        <div className="py-2">
                          <a href="/hesabim" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700" onClick={(e) => { e.preventDefault(); setUserDropdownOpen(false); window.location.href = '/hesabim'; }}><User className="size-5 text-pink-600" /><span>Hesabım</span></a>
                          <a href="/hesabim?tab=orders" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700" onClick={(e) => { e.preventDefault(); setUserDropdownOpen(false); window.location.href = '/hesabim?tab=orders'; }}><Package className="size-5 text-pink-600" /><span>Siparişlerim</span></a>
                          <a href="/favoriler" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700" onClick={(e) => { e.preventDefault(); setUserDropdownOpen(false); window.location.href = '/favoriler'; }}><Heart className="size-5 text-pink-600" /><span>Favorilerim</span></a>
                          <a href="/hesabim?tab=settings" className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors text-gray-700" onClick={(e) => { e.preventDefault(); setUserDropdownOpen(false); window.location.href = '/hesabim?tab=settings'; }}><Settings className="size-5 text-pink-600" /><span>Ayarlar</span></a>
                          <div className="border-t border-gray-100 mt-2 pt-2">
                            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 hover:bg-red-50 transition-colors text-red-600 w-full"><LogOut className="size-5" /><span className="font-semibold">Çıkış Yap</span></button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <a href="/hesabim" className="hidden md:flex items-center gap-2 text-white hover:text-gray-200 transition-colors bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                    <User className="size-5" /><span className="text-sm font-medium">Giriş Yap</span>
                  </a>
                )}

                <a href="/favoriler" className="text-white hover:text-gray-200 transition-colors hidden md:block"><Heart className="size-6" /></a>
                <a href="/sepet" className="text-white hover:text-gray-200 transition-colors relative">
                  <ShoppingCart className="size-6" />
                  {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-pink-600 text-white text-xs rounded-full size-5 flex items-center justify-center font-bold">{cartCount}</span>}
                </a>
                <select className="bg-white/20 text-white border border-white/40 rounded px-2 py-1 text-sm outline-none">
                  <option className="text-gray-900">TR</option><option className="text-gray-900">EN</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="hidden md:flex items-center gap-2 py-3 overflow-x-auto">
            {categories.map((cat) => (
              <button key={cat.id} onClick={() => handleCategoryClick(cat.id)}
                className={`px-6 py-2 rounded-full font-medium transition-colors whitespace-nowrap ${
                  cat.id === 'kesfet'
                    ? 'text-pink-600 hover:bg-pink-50 flex items-center gap-1.5'
                    : activeCategory === cat.id ? 'bg-pink-500 text-white' : 'text-gray-700 hover:bg-gray-100'
                }`}>
                {cat.id === 'kesfet' && <Sparkles className="size-4" />}
                {cat.name}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Mobile Full Screen Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-white z-50 overflow-y-auto">
          <div className="min-h-screen">
            <div className="relative bg-cover bg-center" style={{ backgroundImage: `url(${headerBg})`, backgroundSize: 'cover', backgroundPosition: 'center' }}>
              <div className="bg-red-900/30 backdrop-blur-sm">
                <div className="px-4 py-6">
                  <div className="flex items-center justify-between">
                    <div className="text-white">
                      <div className="text-3xl font-bold tracking-wider">LADIORA</div>
                      <div className="text-sm tracking-widest">BOUTIQUE</div>
                    </div>
                    <button onClick={() => setMobileMenuOpen(false)} className="text-white hover:text-gray-200 transition-colors"><X className="size-8" /></button>
                  </div>
                </div>
              </div>
            </div>

            <div className="px-4 py-6">
              {isAuthenticated ? (
                <div className="mb-6 bg-gradient-to-r from-pink-500 to-purple-500 rounded-2xl p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="size-12 bg-white/20 rounded-full flex items-center justify-center"><User className="size-6" /></div>
                    <div>
                      <p className="font-bold text-lg">Hoşgeldin!</p>
                      <p className="text-sm">{kullanici.ad} {kullanici.soyad}</p>
                    </div>
                  </div>
                  <p className="text-sm opacity-90">{kullanici.email}</p>
                </div>
              ) : (
                <div className="mb-6">
                  <a href="/hesabim" onClick={() => setMobileMenuOpen(false)} className="flex items-center justify-center gap-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white py-4 rounded-xl font-semibold shadow-lg">
                    <User className="size-5" />Giriş Yap / Üye Ol
                  </a>
                </div>
              )}

              <div className="mb-6 grid grid-cols-3 gap-3">
                <a href="/favoriler" onClick={() => setMobileMenuOpen(false)} className="flex flex-col items-center gap-2 bg-pink-50 py-4 rounded-xl hover:bg-pink-100 transition-colors">
                  <Heart className="size-6 text-pink-600" /><span className="text-xs font-medium text-gray-900">Favoriler</span>
                </a>
                <a href="/sepet" onClick={() => setMobileMenuOpen(false)} className="flex flex-col items-center gap-2 bg-pink-50 py-4 rounded-xl hover:bg-pink-100 transition-colors relative">
                  <ShoppingCart className="size-6 text-pink-600" /><span className="text-xs font-medium text-gray-900">Sepetim</span>
                  {cartCount > 0 && <span className="absolute top-2 right-2 bg-pink-600 text-white text-xs rounded-full size-5 flex items-center justify-center font-bold">{cartCount}</span>}
                </a>
                <a href="/siparis-takip" onClick={() => setMobileMenuOpen(false)} className="flex flex-col items-center gap-2 bg-pink-50 py-4 rounded-xl hover:bg-pink-100 transition-colors">
                  <Package className="size-6 text-pink-600" /><span className="text-xs font-medium text-gray-900">Siparişler</span>
                </a>
              </div>

              <div className="mb-6">
                <h3 className="font-bold text-gray-900 mb-3 px-2">Kategoriler</h3>
                <div className="space-y-2">
                  {categories.map((cat) => (
                    <button key={cat.id} onClick={() => { handleCategoryClick(cat.id); setMobileMenuOpen(false); }}
                      className={`w-full px-6 py-3 rounded-xl font-medium text-left transition-all ${activeCategory === cat.id ? 'bg-pink-500 text-white shadow-lg' : 'bg-gray-50 text-gray-900 hover:bg-gray-100'}`}>
                      {cat.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mb-6 border-t border-gray-200 pt-4">
                <h3 className="font-bold text-gray-900 mb-3 px-2">Menü</h3>
                <div className="space-y-1">
                  <a href="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><Home className="size-5 text-pink-600" /><span>Ana Sayfa</span></a>
                  {isAuthenticated && <a href="/hesabim" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><User className="size-5 text-pink-600" /><span>Hesabım</span></a>}
                  <a href="/hakkimizda" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><MapPin className="size-5 text-pink-600" /><span>Hakkımızda</span></a>
                  <a href="/iletisim" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><Phone className="size-5 text-pink-600" /><span>İletişim</span></a>
                  <a href="/sss" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><HelpCircle className="size-5 text-pink-600" /><span>Sık Sorulan Sorular</span></a>
                </div>
              </div>

              <div className="mb-6 border-t border-gray-200 pt-4">
                <h3 className="font-bold text-gray-900 mb-3 px-2">Ayarlar</h3>
                <div className="space-y-1">
                  {isAuthenticated && <a href="/hesabim?tab=settings" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-3 px-4 py-3 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"><Settings className="size-5 text-pink-600" /><span>Hesap Ayarları</span></a>}
                  <div className="flex items-center justify-between px-4 py-3">
                    <span className="text-gray-700 font-medium">Dil / Language</span>
                    <select className="bg-gray-100 text-gray-900 border border-gray-300 rounded-lg px-3 py-2 text-sm outline-none">
                      <option>🇹🇷 Türkçe</option><option>🇬🇧 English</option>
                    </select>
                  </div>
                </div>
              </div>

              {isAuthenticated && (
                <div className="border-t border-gray-200 pt-4">
                  <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full flex items-center justify-center gap-3 bg-red-50 text-red-600 py-4 rounded-xl font-semibold hover:bg-red-100 transition-colors">
                    <LogOut className="size-5" />Çıkış Yap
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}